/**
 * Onboard — add devices to the current site, with a naming TEMPLATE + auto group tags.
 *
 * "Add any device to this site": both the UNASSIGNED pool and devices in another site (moved
 * here). The onboarding template makes it EFFICIENT (the team's #1 ask): give a naming pattern
 * like `{site}-{kind}-{seq2}` and a set of group tags, see a live preview of the resulting
 * hostnames, and apply them to the whole batch on Add — no per-device renaming. All audited.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSession } from "@/stores/session";
import { useAssignableDevices, useAssignDevices, usePreregs, useAddPreregs, useDeletePrereg } from "@/lib/api/hooks";
import { api } from "@/lib/api/client";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { RoleGate } from "@/components/auth/RoleGate";
import {
  CLAIM_HINT,
  CLAIM_LABEL,
  CLAIM_PLACEHOLDER,
  invalidClaimMessage,
  parseClaimInput,
} from "@/lib/claimIdentifiers";
import type { AssignableDevice } from "@/types/domain";
import { QUARANTINE_LABEL } from "@/lib/quarantine";
import { moveResultLines } from "@/lib/deviceMove";
import { CustomerDefaultOptIn } from "@/components/device/CustomerDefaultOptIn";

interface PreviewRow { serial: string; name: string; kind: string }

export default function Onboard() {
  const siteId = useSession((s) => s.currentSiteId);
  const siteName = useSession((s) => s.currentSite()?.name);
  const { data, isLoading, error } = useAssignableDevices(siteId);
  const assign = useAssignDevices();
  const orgId = useSession((s) => s.currentOrgId);
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pattern, setPattern] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const [fwPolicy, setFwPolicy] = useState("off");
  // OP#1935 opt-in for MOVES (devices already placed in another site); off by default.
  const [applyDefault, setApplyDefault] = useState(false);

  // "Claim new devices" -- reserve devices that have NOT checked in yet (pre-registration), so
  // they auto-land in this site the instant they power on. This is the entry point new kit needs:
  // the lists below only show devices owgw has ALREADY seen, so before this panel a freshly
  // shipped unit had nowhere to be added from (the team's onboarding-UX gap, 2026-09-29). Keyed
  // on the MAC because uCentral devices report their serial to owgw AS the colon-less MAC -- a
  // vendor serial (RAP750..., RSW226...) never matches and the reservation would sit dead.
  const [claimText, setClaimText] = useState("");
  const preregs = usePreregs(siteId);
  const addPreregs = useAddPreregs(siteId);
  const delPrereg = useDeletePrereg(siteId);
  // Shared with the Sites page "Pre-register devices" dialog (lib/claimIdentifiers): MACs only,
  // because only a MAC can ever match (UI review 2026-10-17). Non-MAC entries block the claim.
  const claimParsed = parseClaimInput(claimText);
  const claimIds = claimParsed.macs;
  const claimInvalid = invalidClaimMessage(claimParsed);
  // Server floor: POST/DELETE /sites/<id>/preregistrations are @require("org_admin")
  // (api/v2/prereg.py). A site manager still sees what is reserved (GET is open to the site's
  // roles) but the claim controls are disabled with the reason, never offered as a dead click.
  const isCustomerAdmin = useSession((s) => s.hasRole("org_admin"));

  const serials = [...selected];
  const tags = tagsInput.split(",").map((t) => t.trim()).filter(Boolean);
  const patternOn = pattern.trim().length > 0;

  // Load the customer's PERSISTED onboarding profile and pre-fill the form from it (set once,
  // reused every time). Only fills empty fields so it never clobbers what the operator typed.
  const profile = useQuery({
    queryKey: ["v2", "onboarding-profile", orgId],
    queryFn: () => api.get<{ naming_pattern: string | null; tags: string[]; firmware_policy: string }>(
      `/orgs/${orgId}/onboarding-profile`),
    enabled: !!orgId,
  });
  useEffect(() => {
    const p = profile.data;
    if (!p) return;
    setPattern((cur) => cur || p.naming_pattern || "");
    setTagsInput((cur) => cur || (p.tags || []).join(", "));
    setFwPolicy(p.firmware_policy || "off");
  }, [profile.data]);

  const saveDefault = useMutation({
    mutationFn: () => api.put(`/orgs/${orgId}/onboarding-profile`,
      { naming_pattern: pattern.trim(), tags, firmware_policy: fwPolicy }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["v2", "onboarding-profile", orgId] }),
  });

  // Live preview of the names the pattern would produce (server-computed so the sequence
  // continues from the site's existing device count — same numbering assign uses).
  const preview = useQuery({
    queryKey: ["v2", "onboard-preview", siteId, pattern.trim(), serials.slice().sort().join(",")],
    queryFn: () =>
      api.post<{ items: PreviewRow[] }>("/devices/name-preview", {
        serials, site_id: Number(siteId), naming_pattern: pattern.trim(),
      }),
    enabled: patternOn && serials.length > 0 && !!siteId,
  });

  if (!siteId) {
    return (
      <div>
        <PageHeader title="Onboard Devices" subtitle="Add devices to a site" />
        <EmptyState
          title="Select a site first"
          hint="Onboarding places devices into a specific site. Pick a site from the scope bar above, then choose which devices to add."
        />
      </div>
    );
  }

  const items = data?.items ?? [];
  const unassigned = items.filter((d) => d.unassigned);
  const elsewhere = items.filter((d) => !d.unassigned);

  const toggle = (serial: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(serial) ? next.delete(serial) : next.add(serial);
      return next;
    });

  // OP#1935: devices picked from "In other sites" are MOVES. The naming/tags/firmware above
  // apply to them only when this (unchecked) box is ticked; new devices are unaffected.
  const movesSelected = elsewhere.some((d) => selected.has(d.serial));

  const onboard = () => {
    if (selected.size === 0) return;
    assign.mutate(
      {
        serials, site_id: Number(siteId),
        ...(patternOn ? { naming_pattern: pattern.trim() } : {}),
        ...(tags.length ? { tags } : {}),
        ...(movesSelected && applyDefault ? { apply_customer_default: true } : {}),
      },
      { onSuccess: () => { setSelected(new Set()); setApplyDefault(false); } },
    );
  };

  const previewByserial = new Map((preview.data?.items ?? []).map((r) => [r.serial, r.name]));

  return (
    <div>
      <PageHeader title="Onboard Devices" subtitle={siteName ? `Add devices to ${siteName}` : undefined} />

      {error && <ErrorBanner error={error} />}

      {/* ---- Claim new devices (pre-registration) ----
          Always shown when a site is selected -- it is the ONLY way to load a device that has
          not checked in yet, which is exactly when the device lists below are empty. */}
      <RoleGate min="site_operator" mode="hide">
        <section className="md-card mb-4 p-4">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">Claim new devices</h2>
          <p className="mt-0.5 text-xs text-ink-muted">
            Have devices that have not checked in yet? Paste their MAC addresses to reserve them
            to {siteName ?? "this site"} — they appear below and adopt automatically the moment they power on.
          </p>
          {!isCustomerAdmin && (
            <p className="mt-2 text-xs text-warn" data-testid="claim-role-note">
              Claiming devices requires a customer admin. You can see what is already reserved below.
            </p>
          )}
          <label className="mt-3 flex flex-col gap-1 text-sm">
            <span className="text-ink-muted">{CLAIM_LABEL}</span>
            <textarea
              value={claimText}
              onChange={(e) => setClaimText(e.target.value)}
              rows={4}
              disabled={!isCustomerAdmin}
              placeholder={CLAIM_PLACEHOLDER}
              className="w-full rounded border border-line bg-surface px-3 py-2 font-mono text-xs text-ink outline-none focus:border-brand disabled:opacity-60"
            />
          </label>
          <p className="mt-1 text-[0.6875rem] text-ink-subtle">{CLAIM_HINT}</p>
          {claimInvalid && <p className="mt-1 text-xs text-critical" role="alert">{claimInvalid}</p>}
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <RoleGate min="org_admin" mode="disable" reason="Claiming devices requires a customer admin.">
              <Button variant="primary" type="button"
                disabled={claimIds.length === 0 || !!claimInvalid || addPreregs.isPending}
                onClick={() => addPreregs.mutate({ identifiers: claimIds }, { onSuccess: () => setClaimText("") })}>
                {addPreregs.isPending ? "Claiming..." : `Claim ${claimIds.length || ""} to ${siteName ?? "site"}`}
              </Button>
            </RoleGate>
            {addPreregs.data && (
              <span className="text-xs text-healthy">
                Reserved {addPreregs.data.added}{addPreregs.data.skipped ? `, skipped ${addPreregs.data.skipped}` : ""}.
              </span>
            )}
            {addPreregs.isError && <span className="text-xs text-critical">Could not reserve -- check the identifiers.</span>}
          </div>

          {(preregs.data?.items?.length ?? 0) > 0 && (
            <div className="mt-3">
              <div className="text-[0.6875rem] font-semibold uppercase tracking-wide text-ink-subtle">
                Reserved, waiting to check in ({preregs.data!.items.length})
              </div>
              <ul className="mt-1 flex flex-col gap-1">
                {preregs.data!.items.map((r) => (
                  <li key={r.id} className="flex items-center gap-2 text-sm">
                    <span className="font-mono text-xs text-ink">{r.raw ?? r.identifier}</span>
                    {/* DELETE /preregistrations/<id> is org_admin too: hidden below it. */}
                    {isCustomerAdmin && (
                      <button type="button" onClick={() => delPrereg.mutate(r.id)}
                        className="text-[0.6875rem] text-ink-subtle hover:text-critical" aria-label="Remove reservation">
                        remove
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      </RoleGate>

      {isLoading && <div className="text-sm text-ink-muted">Loading devices…</div>}

      {data && items.length === 0 && (
        <EmptyState
          title="No devices to add"
          hint="Every device you manage is already in this site. Newly registered devices will appear here."
        />
      )}

      {items.length > 0 && (
        <>
          {/* ---- Onboarding template ---- */}
          <RoleGate min="site_operator" mode="hide">
            <section className="md-card mb-4 p-4">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">Onboarding template</h2>
              <p className="mt-0.5 text-xs text-ink-muted">
                Auto-name and group every selected device on Add. Leave blank to keep current names.
              </p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-ink-muted">Naming pattern</span>
                  <input value={pattern} onChange={(e) => setPattern(e.target.value)}
                    placeholder="{site}-{kind}-{seq2}"
                    className="rounded border border-line bg-surface px-3 py-2 font-mono text-ink outline-none focus:border-brand" />
                  <span className="text-[0.6875rem] text-ink-subtle">
                    tokens: {"{site} {org} {kind} {model} {seq} {seq2} {seq3} {mac4} {mac6}"}
                  </span>
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-ink-muted">Group tags (comma-separated)</span>
                  <input value={tagsInput} onChange={(e) => setTagsInput(e.target.value)}
                    placeholder="lobby, guest-wifi"
                    className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand" />
                  {tags.length > 0 && (
                    <span className="flex flex-wrap gap-1">
                      {tags.map((t) => (
                        <span key={t} className="rounded bg-surface-2 px-1.5 py-0.5 text-[0.6875rem] text-ink-muted">{t}</span>
                      ))}
                    </span>
                  )}
                </label>
              </div>

              {/* Firmware policy + persist-as-customer-default */}
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 text-sm">
                  <span className="text-ink-muted">Firmware on adopt</span>
                  <select value={fwPolicy} onChange={(e) => setFwPolicy(e.target.value)}
                    className="rounded border border-line bg-surface px-2 py-1.5 text-ink outline-none focus:border-brand">
                    <option value="off">Leave as-is</option>
                    <option value="latest-stable">Upgrade to latest stable</option>
                    <option value="latest">Upgrade to latest</option>
                  </select>
                </label>
                {/* org_admin (OP#1580): PUT /orgs/<id>/onboarding-profile is the customer-wide
                    default, so the server refuses a site_operator. Shown disabled with the reason. */}
                <RoleGate min="org_admin" mode="disable"
                  reason="Saving the customer default requires a customer admin.">
                  <Button variant="secondary" type="button" disabled={saveDefault.isPending}
                    onClick={() => saveDefault.mutate()}>
                    {saveDefault.isPending ? "Saving…" : "Save as customer default"}
                  </Button>
                </RoleGate>
                {saveDefault.isSuccess && <span className="text-xs text-healthy">Saved — applies to every future onboard.</span>}
              </div>

              {patternOn && serials.length > 0 && (
                <div className="mt-3">
                  <div className="text-[0.6875rem] font-semibold uppercase tracking-wide text-ink-subtle">Preview</div>
                  <ul className="mt-1 flex flex-col gap-0.5 text-sm">
                    {serials.map((s) => (
                      <li key={s} className="flex items-center gap-2">
                        <span className="font-mono text-xs text-ink-subtle">{s}</span>
                        <span className="text-ink-subtle">→</span>
                        <span className="font-mono text-ink">{previewByserial.get(s) ?? (preview.isLoading ? "…" : "—")}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          </RoleGate>

          {/* ---- Add action ---- */}
          <div className="mb-3 flex items-center justify-end">
            <RoleGate min="site_operator" mode="hide">
              <div className="flex items-center gap-2">
                {assign.data && (
                  <span className="text-xs text-healthy" data-testid="onboard-result">
                    Added {assign.data.assigned}
                    {assign.data.skipped ? `, skipped ${assign.data.skipped}` : ""}.
                    {/* OP#1935: what was actually applied, incl. firmware_queued. */}
                    {moveResultLines(assign.data, siteName ?? "").slice(1).map((l) => ` ${l}`).join("")}
                  </span>
                )}
                {assign.isError && <span className="text-xs text-critical">Failed.</span>}
                {/* POST /devices/assign is @require("org_admin"): disabled with the reason below it. */}
                {!isCustomerAdmin && (
                  <span className="text-xs text-warn" data-testid="assign-role-note">Adding or moving devices between sites requires a customer admin.</span>
                )}
                <RoleGate min="org_admin" mode="disable" reason="Adding or moving devices between sites requires a customer admin.">
                  <Button variant="primary" disabled={selected.size === 0 || assign.isPending} onClick={onboard}>
                    {assign.isPending ? "Adding…" : `Add ${selected.size || ""} to ${siteName ?? "site"}`}
                  </Button>
                </RoleGate>
              </div>
            </RoleGate>
          </div>

          {unassigned.length > 0 && (
            <Group title="Unassigned" hint="Not yet placed at any site">
              {unassigned.map((d) => (
                <DeviceRow key={d.serial} device={d} checked={selected.has(d.serial)}
                  onToggle={() => toggle(d.serial)} previewName={patternOn ? previewByserial.get(d.serial) : undefined} />
              ))}
            </Group>
          )}

          {movesSelected && (
            <div className="mb-3">
              <CustomerDefaultOptIn orgId={orgId} checked={applyDefault} onChange={setApplyDefault}
                testId="onboard-move-optin" />
            </div>
          )}

          {elsewhere.length > 0 && (
            <Group title="In other sites" hint="Currently placed elsewhere — moving brings them here">
              {elsewhere.map((d) => (
                <DeviceRow key={d.serial} device={d} checked={selected.has(d.serial)}
                  onToggle={() => toggle(d.serial)} previewName={patternOn ? previewByserial.get(d.serial) : undefined} />
              ))}
            </Group>
          )}
        </>
      )}
    </div>
  );
}

function Group({ title, hint, children }: { title: string; hint: string; children: React.ReactNode }) {
  return (
    <section className="mb-5">
      <div className="mb-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">{title}</h2>
        <p className="text-xs text-ink-muted">{hint}</p>
      </div>
      <ul className="flex flex-col gap-2">{children}</ul>
    </section>
  );
}

function DeviceRow({
  device, checked, onToggle, previewName,
}: {
  device: AssignableDevice;
  checked: boolean;
  onToggle: () => void;
  previewName?: string;
}) {
  return (
    <li className="md-card flex items-center gap-3 p-3">
      <input type="checkbox" checked={checked} onChange={onToggle} aria-label={`Select ${device.serial}`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium text-ink">{device.name}</span>
          {checked && previewName && (
            <>
              <span className="text-ink-subtle">→</span>
              <span className="truncate font-mono text-xs text-brand">{previewName}</span>
            </>
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-ink-muted">
          <span className="font-mono">{device.serial}</span>
          <span>{device.model ?? "—"}</span>
          {device.ip && <span className="font-mono">{device.ip}</span>}
          <span className="text-ink-subtle">
            {device.unassigned
              ? "unassigned"
              : // A device in the holding area is not "in" a customer (2026-10-19).
                `now in ${device.current_customer_is_quarantine ? QUARANTINE_LABEL : device.current_customer} · ${device.current_site}`}
          </span>
        </div>
      </div>
      <span className={device.online ? "text-xs text-healthy" : "text-xs text-ink-subtle"}>
        {device.online ? "online" : "offline"}
      </span>
    </li>
  );
}
